import { Fragment, useState } from 'react';
import { Th, Cell } from './Cells';
import { Parts } from './Parts';
import { DrillTable } from './DrillTable';
import { DrillCaret } from './DrillParts';
import { ShowAll } from './ShowAll';
import { useSort } from './useSort';
import { usePreview } from './usePreview';

const EMPTY = Object.freeze([]);

/**
 * The table every screen draws with. See index.js for the rules it enforces;
 * this is the contract.
 *
 * COLUMNS — `{ key, label, hint?, num?, className?, bold?, render?, cell?, sort?, tone? }`
 *   see Cells.js. `ctx` handed to `render` and `cell` is `{ open }` on a
 *   roll-up row and `{ parent }` on a part. The first column must use `render`
 *   when the table expands or pins: that is the cell the caret goes in.
 *
 * ROWS — `rows`, `rowKey(row, i)`, and how a row behaves:
 *   `onRowClick(row)` makes rows a target (`rowClickable(row)` narrows which);
 *   `rowTitle(row)` is the hover text; `rowNote(row)` a small second line under
 *   the first cell; `rowClassName(row)` an extra class. A row nothing happens
 *   on keeps the reading cursor.
 *
 * SORT — `sort: { key, desc }` is the initial order; headers of columns with a
 *   `sort` picker toggle it.
 *
 * PREVIEW — `preview` rows show first (a number, or a function picking the
 *   subset worth showing), then a footer strip asks "Show all N <noun>" and
 *   "Show fewer". `footer` is a note that sits in that strip beside the button.
 *   `empty` is drawn in place of the rows when none show.
 *
 * EXPAND — a roll-up row opens into its parts as rows of the same table; see
 *   Parts.js for the `expand` contract. A row with no parts is not expandable.
 *
 * TOTAL — `total` is a row that closes the whole table: an object keyed by
 *   column key whose values are content or `{ value, tone, bold }`.
 *
 * FRAME — `pinFirst` holds the first column while the rest scroll, `minWidth`
 *   makes a wide table scroll rather than squeeze, `className` adds to the
 *   table, `scrollRef` reaches the scroller.
 */
export function DataTable({
  columns, rows, rowKey,
  sort: initialSort = null,
  preview = false, noun = 'rows', footer = null, empty = null,
  onRowClick, rowClickable, rowTitle, rowNote, rowClassName,
  expand = null, total = null,
  pinFirst = false, minWidth, className, scrollRef,
}) {
  const sorting = useSort(rows, columns, initialSort);
  const { shown, all, total: count, toggle } = usePreview(sorting.sorted, preview);
  const [open, setOpen] = useState(null);
  const sortable = columns.some((c) => c.sort);
  const span = columns.length;

  const partsOf = (row) => (expand ? expand.children(row) ?? EMPTY : EMPTY);
  const afterOf = (row) => (expand?.after ? expand.after(row) ?? EMPTY : EMPTY);
  const expandable = (row) => Boolean(expand) && (partsOf(row).length > 0 || afterOf(row).length > 0);
  const showFoot = footer || all || shown.length < count;

  return (
    <>
      <DrillTable className={className} minWidth={minWidth} ref={scrollRef}>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <Th key={col.key} col={col} sort={sortable ? sorting : null} pin={pinFirst && i === 0} />
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && empty && (
            <tr className="dt-static">
              <td colSpan={span} style={{ padding: 0 }}>{empty}</td>
            </tr>
          )}

          {shown.map((row, index) => {
            const key = rowKey(row, index);
            const isOpen = open === key;
            const opens = expandable(row);
            const clicks = !opens && Boolean(onRowClick) && (rowClickable ? rowClickable(row) : true);
            const onClick = opens
              ? () => setOpen(isOpen ? null : key)
              : clicks ? () => onRowClick(row) : undefined;
            const cls = [onClick ? '' : 'dt-static', rowClassName?.(row) || ''].filter(Boolean).join(' ');
            const ctx = { open: isOpen };
            return (
              <Fragment key={key}>
                <tr
                  className={cls || undefined}
                  onClick={onClick}
                  style={onClick ? { cursor: 'pointer' } : undefined}
                  title={rowTitle?.(row)}
                  aria-expanded={opens ? isOpen : undefined}
                >
                  {columns.map((col, i) => (
                    <Cell
                      key={col.key}
                      col={col}
                      row={row}
                      ctx={ctx}
                      caret={i === 0 && expand ? <DrillCaret open={isOpen} show={opens} /> : null}
                      note={i === 0 ? rowNote?.(row) : null}
                      pin={pinFirst && i === 0}
                    />
                  ))}
                </tr>
                {/* Keyed on the row, so the parts' sort and show-all start
                    afresh for each roll-up that is opened. */}
                {isOpen && (
                  <Parts key={`${key}:parts`} parent={row} expand={expand} parentColumns={columns} span={span} />
                )}
              </Fragment>
            );
          })}

          {total && (
            <tr className="drill-total dt-static">
              {columns.map((col) => {
                const v = total[col.key];
                const spec = v != null && typeof v === 'object' && !v.$$typeof && 'value' in v ? v : null;
                const content = spec ? spec.value : v;
                const cls = [col.num ? 'num' : '', spec?.tone || ''].filter(Boolean).join(' ');
                return (
                  <td key={col.key} className={cls || undefined}>
                    {spec?.bold ? <b>{content}</b> : content ?? null}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </DrillTable>

      {showFoot && (
        <div className="dt-foot">
          {footer && <div className="dt-foot-note">{footer}</div>}
          <ShowAll total={count} shown={shown.length} all={all} onToggle={toggle} noun={noun} />
        </div>
      )}
    </>
  );
}
